import type { FormFieldOption, FormFieldOptions } from '@/form/useForm'
import { computed, ref, watch, type MaybeRef, type Ref } from 'vue'
import { getRefId } from 'findef'
import { isNullish } from '@/utils/is'
import { unref } from 'vue'
import { useAsyncList } from '@/utils/async/useAsyncList'
import { lerp, smoothstep, unique, uniqueBy } from 'math'
import type { ComputedRef } from 'vue'
import { EventProducer } from '@/utils/event'
import { debouncedRef } from '@vueuse/core'
import { useAnimationStep } from '@/utils/animation/useAnimationStep'

export type UseMultiSelectProps = {
  values?: MaybeRef<any[]> | null | undefined;
  disabled?: MaybeRef<boolean>;
  options: FormFieldOptions
  maxVisibleItems?: number
}

export type UseMultiSelect = {
  selected: ComputedRef<FormFieldOption[]>
  items: Ref<FormFieldOption[]>
  select: (value: any) => void
  unselect: (value: any) => void
  toggleSelect: (value: any) => void
  setQueryString: (queryString: string) => void
  maxVisibleItems: ComputedRef<number>
  isFetching: ComputedRef<boolean>
  isAdding: Ref<boolean>
  events: EventProducer<MultiSelectEventMap>;
  disabled: ComputedRef<boolean>;
  addManually?:
    | ((value: string) => Promise<FormFieldOption | undefined> | (FormFieldOption | undefined))
    | null
    | undefined
  queryString: Ref<string>
  elapsedProgress: ComputedRef<number>
}

const getKey = (x: any): string => {
  if (isNullish(x)) return ''
  if (typeof x === 'string') return x
  if (typeof x === 'object') {
    const k = getRefId(x)
    if (k !== '') return k
  }
  try {
    return JSON.stringify(x)
  } catch (e) {
    console.error(e)
    return ''
  }
}

export enum EMultiSelectEvent {
  SELECTION_CHANGE = 'SELECTION_CHANGE'
}

export type MultiSelectEventMap = {
  [EMultiSelectEvent.SELECTION_CHANGE]: {
    selection: any[]
  }
}

export const useMultiSelect = (props: UseMultiSelectProps): UseMultiSelect => {
  const events = new EventProducer<MultiSelectEventMap>();

  const disabled = computed(() => !!unref(props.disabled));

  const loadingProgress = useAnimationStep({
    initial: 0,
    duration: 0.5
  })
  const isAdding = ref<boolean>(false)
  const elapsedProgress = computed(() => {
    return smoothstep(0.0, 1.0, loadingProgress.current.value)
  })

  const maxVisibleItems = computed(() => unref(props.maxVisibleItems) || 4)
  const queryString = ref<string>('')
  const setQueryString = (q: string) => {
    queryString.value = q
  }
  const debouncedQueryString = debouncedRef(queryString, 160)

  const manualOptions = ref<FormFieldOption[]>([])

  const fetchedItems__all = useAsyncList(async () => {
    if (!props.options?.search?.api?.fetchAll) {
      return []
    }
    const its = await props.options?.search?.api?.fetchAll()
    return its
  })

  const fetchedItems__queried = useAsyncList(async () => {
    if (!props.options?.search?.api?.fetch) {
      return []
    }
    const its = await props.options?.search?.api?.fetch(debouncedQueryString.value)
    return its
  }, [debouncedQueryString])

  const refresh = async () => {
    await fetchedItems__all.refresh()
    await fetchedItems__queried.refresh()
  }

  const isFetching = computed(
    (): boolean => !!fetchedItems__all.loading.value || !!fetchedItems__queried.loading.value
  )

  const values = computed(() => unref(props.values) || [])

  const selectedKeys = ref<any[]>([])

  const items = computed(() => {
    let its: FormFieldOption[] = props.options?.items || []

    if (
      props.options?.search?.enabled !== false &&
      props.options?.search?.api?.fetch &&
      queryString.value.length > 0
    ) {
      its = fetchedItems__queried.items.value
    } else if (props.options?.search?.enabled !== false && props.options?.search?.api?.fetchAll) {
      its = fetchedItems__all.items.value
    }

    const computedItems = unref(props.options?.computedItems) || [];
    
    its = [...(manualOptions.value as any[]), ...computedItems, ...its];

    return uniqueBy(
      its.map((it) => ({
        ...it,
        selected: selectedKeys.value.includes(it.key || getRefId(it.value))
      })),
      (it) => it.key || getRefId(it.value) || JSON.stringify(it)
    )
  })

  const selected = computed((): FormFieldOption[] => items.value.filter((it) => !!it.selected))

  const emitSelectionChange = () => {
    events.emit(EMultiSelectEvent.SELECTION_CHANGE, {
      selection: selected.value.map((it) => it.value)
    })
  }

  const select = (value: any) => {
    if (isNullish(value)) {
      selectedKeys.value = []
      return
    }
    selectedKeys.value = unique([...selectedKeys.value, getKey(value)])
    emitSelectionChange()
  }

  const unselect = (value: any) => {
    selectedKeys.value = selectedKeys.value.filter((it) => it !== getKey(value))
    emitSelectionChange()
  }

  const toggleSelect = (value: any) => {
    const key = getKey(value)
    if (selectedKeys.value.includes(key)) {
      unselect(value)
    } else {
      select(value)
    }
  }

  const addManually = props.options?.add?.createItem
    ? async (value: string) => {
        isAdding.value = true
        
        const fun = props.options?.add?.createItem as (
          value: string
        ) => Promise<FormFieldOption | undefined> | (FormFieldOption | undefined)
      const item = await fun(value);
        await loadingProgress.to(0.25, 0.33, (start, target, mix) =>
          lerp(start, target, smoothstep(0.0, 1.0, mix))
        )
        if (item) {
          (manualOptions.value as any[]).push(item);
          queueMicrotask(() => {
            select(item.value);
          })
        }
        await loadingProgress.to(0.5, 0.33)
        await refresh()
        await loadingProgress.to(1.0, 0.15).then(() => {
          loadingProgress.to(0.0, 0.001)
          isAdding.value = false
          loadingProgress.set(0.0);
        });
        return item
      }
    : undefined

  watch(
    values,
    (values) => {
      select(null)
      ;(values || []).forEach((value) => {
        select(value)
      })
    },
    { immediate: true }
  )

  return {
    selected,
    items,
    select,
    unselect,
    toggleSelect,
    setQueryString,
    maxVisibleItems,
    isFetching,
    isAdding,
    events,
    addManually,
    queryString,
    elapsedProgress,
    disabled
  }
}
